/**
 * Maps API errors to user-friendly messages
 */
export function getErrorMessage(error: unknown, defaultMessage?: string): string {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return mapErrorToUserMessage(error);
  }

  // If it's an Error object
  if (error instanceof Error) {
    return mapErrorToUserMessage(error.message);
  }

  // If it's an object with a message property
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message);
      return mapErrorToUserMessage(message);
  }

  // If it's an object with an error property
  if (error && typeof error === 'object' && 'error' in error) {
    const message = String((error as { error: unknown }).error);
    return mapErrorToUserMessage(message);
  }

  // Default fallback
  return defaultMessage || 'An unexpected error occurred. Please try again.';
}

/**
 * Maps specific error messages to user-friendly messages
 */
function mapErrorToUserMessage(errorMessage: string): string {
  const lowerMessage = errorMessage.toLowerCase();

  // Check for authentication/login errors FIRST (before generic 401 handling)
  // This handles login failures with invalid credentials
  if (lowerMessage.includes('invalid email') || lowerMessage.includes('invalid password') || 
      lowerMessage.includes('incorrect email') || lowerMessage.includes('incorrect password') ||
      lowerMessage.includes('wrong email') || lowerMessage.includes('wrong password') ||
      lowerMessage.includes('email or password') || lowerMessage.includes('credentials')) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  // 403 Forbidden errors
  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return 'Please check that you filled all the required fields correctly.';
  }

  // 401 Unauthorized errors - but only for non-login scenarios (expired sessions)
  // Login errors should have been caught above
  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    // If this is clearly a login attempt (has email/password/login keywords), show credential error
    if (lowerMessage.includes('email') || lowerMessage.includes('password') || 
        lowerMessage.includes('login') || lowerMessage.includes('authenticate')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    return 'Your session has expired. Please log in again.';
  }

  // 404 Not Found errors - but check for specific auth errors first
  if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    // Check if it's an auth-related 404 error
    if (lowerMessage.includes('email') || lowerMessage.includes('password') || lowerMessage.includes('login') || lowerMessage.includes('authenticate')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    return 'The requested item could not be found.';
  }

  // 400 Bad Request errors
  if (lowerMessage.includes('bad request') || lowerMessage.includes('400')) {
    if (lowerMessage.includes('required') || lowerMessage.includes('missing')) {
      return 'Please check that you filled all the required fields correctly.';
    }
    if (lowerMessage.includes('invalid') || lowerMessage.includes('validation')) {
      return 'Please check that all fields are filled correctly.';
    }
    return 'Invalid request. Please check your input and try again.';
  }

  // 500 Server errors
  if (lowerMessage.includes('internal server error') || lowerMessage.includes('500')) {
    return 'A server error occurred. Please try again later.';
  }

  // Network errors
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    return 'Network error. Please check your internet connection and try again.';
  }

  // File upload errors
  if (lowerMessage.includes('file') || lowerMessage.includes('upload')) {
    if (lowerMessage.includes('size') || lowerMessage.includes('too large')) {
      return 'File size is too large. Please upload a smaller file.';
    }
    if (lowerMessage.includes('type') || lowerMessage.includes('format')) {
      return 'Invalid file type. Please upload a supported file format.';
    }
    return 'File upload failed. Please try again.';
  }

  // Validation errors
  if (lowerMessage.includes('validation') || lowerMessage.includes('validate')) {
    return 'Please check that all fields are filled correctly.';
  }

  // Duplicate/Conflict errors
  if (lowerMessage.includes('duplicate') || lowerMessage.includes('already exists') || lowerMessage.includes('409')) {
    return 'This item already exists. Please use a different value.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return 'Request timed out. Please try again.';
  }

  // If the error message is already user-friendly, return it
  if (errorMessage.length < 100 && !errorMessage.includes('API Error') && !errorMessage.includes('Error:')) {
    return errorMessage;
  }

  // Extract meaningful parts from technical error messages
  // Handle formats like: "API Error: 404 . Invalid email or password" or "API Error: 404 Invalid email or password"
  // Match "API Error: " followed by status code, optional period/space, then the actual message
  const apiErrorMatch = errorMessage.match(/API\s*Error:\s*\d+\s*\.?\s*(.+)/i);
  if (apiErrorMatch && apiErrorMatch[1]) {
    let extractedMessage = apiErrorMatch[1].trim();
    // Remove leading period and any extra whitespace if present
    extractedMessage = extractedMessage.replace(/^\.\s*/, '').trim();
    
    // Check for login/auth specific messages first
    const lowerExtracted = extractedMessage.toLowerCase();
    if (lowerExtracted.includes('invalid email') || lowerExtracted.includes('invalid password') ||
        lowerExtracted.includes('incorrect email') || lowerExtracted.includes('incorrect password') ||
        lowerExtracted.includes('wrong email') || lowerExtracted.includes('wrong password') ||
        lowerExtracted.includes('email or password') || lowerExtracted.includes('credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    
    // Return the extracted message if it's reasonable length
    if (extractedMessage.length < 150 && extractedMessage.length > 0) {
      return extractedMessage;
    }
  }

  // Default fallback for unrecognized errors
  return 'An error occurred. Please try again or contact support if the problem persists.';
}

/**
 * Extracts error message from API response
 */
export function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return getErrorMessage(error);
  }

  if (error && typeof error === 'object') {
    // Try to extract from common error response structures
    if ('message' in error) {
      return getErrorMessage((error as { message: unknown }).message);
    }
    if ('error' in error) {
      return getErrorMessage((error as { error: unknown }).error);
    }
    if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
      return getErrorMessage((error.data as { message: unknown }).message);
    }
  }

  return getErrorMessage(error);
}

